import { formatDistanceToNow, format, parseISO, isValid } from 'date-fns'

const MONTHS = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
]

const MONTHS_FULL = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
]

function parseDate(value: string | Date | null | undefined): Date | null {
  if (!value) return null
  if (value instanceof Date) return isValid(value) ? value : null
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : null
}

/**
 * Format date as "DD Mon YYYY" (e.g., "15 Jan 2025")
 */
export function formatDate(value: string | Date | null | undefined): string {
  const date = parseDate(value)
  if (!date) return '—'
  const day = String(date.getDate()).padStart(2, '0')
  const month = MONTHS[date.getMonth()]
  const year = date.getFullYear()
  return `${day} ${month} ${year}`
}

/**
 * Format date+time as "DD Mon YYYY, HH:MM" (e.g., "15 Jan 2025, 14:30")
 */
export function formatDateTime(value: string | Date | null | undefined): string {
  const date = parseDate(value)
  if (!date) return '—'
  const day = String(date.getDate()).padStart(2, '0')
  const month = MONTHS[date.getMonth()]
  const year = date.getFullYear()
  const hours = String(date.getHours()).padStart(2, '0')
  const minutes = String(date.getMinutes()).padStart(2, '0')
  return `${day} ${month} ${year}, ${hours}:${minutes}`
}

/**
 * Compact date+time for table cells: "8 Jun, 2:30 PM" (12-hour, no year)
 */
export function formatDateTimeShort(value: string | Date | null | undefined): string {
  const date = parseDate(value)
  if (!date) return '—'
  const day = date.getDate()
  const month = MONTHS[date.getMonth()]
  let h = date.getHours()
  const m = String(date.getMinutes()).padStart(2, '0')
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12 || 12
  return `${day} ${month}, ${h}:${m} ${ampm}`
}

/**
 * Full date+time for hover tooltips: "8 June 2026, 2:30 PM"
 */
export function formatDateTimeFull(value: string | Date | null | undefined): string {
  const date = parseDate(value)
  if (!date) return '—'
  const day = date.getDate()
  const month = MONTHS_FULL[date.getMonth()]
  const year = date.getFullYear()
  let h = date.getHours()
  const m = String(date.getMinutes()).padStart(2, '0')
  const ampm = h >= 12 ? 'PM' : 'AM'
  h = h % 12 || 12
  return `${day} ${month} ${year}, ${h}:${m} ${ampm}`
}

/**
 * Format a number as currency (e.g., "$1,234.56")
 */
export function formatCurrency(
  value: number | null | undefined,
  currency = 'USD',
  locale = 'en-US'
): string {
  if (value === null || value === undefined || isNaN(value)) return '—'
  return new Intl.NumberFormat(locale, {
    style: 'currency',
    currency,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(value)
}

/**
 * Format a phone number in a readable format
 * Handles US: (123) 456-7890, international: +1 234 567 8900
 */
export function formatPhone(value: string | null | undefined): string {
  if (!value) return '—'
  // Remove all non-digits
  const digits = value.replace(/\D/g, '')

  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
  }
  if (digits.length === 11 && digits[0] === '1') {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`
  }
  if (digits.length > 10) {
    return `+${digits.slice(0, digits.length - 10)} ${digits.slice(-10, -7)} ${digits.slice(-7, -4)} ${digits.slice(-4)}`
  }
  return value
}

/**
 * Truncate a string to maxLen characters, appending ellipsis
 */
export function truncate(value: string | null | undefined, maxLen = 50): string {
  if (!value) return ''
  if (value.length <= maxLen) return value
  return value.slice(0, maxLen - 3) + '...'
}

/**
 * Get initials from a full name (up to 2 characters)
 * "John Doe" -> "JD", "Alice" -> "AL", "John Michael Doe" -> "JD"
 */
export function getInitials(name: string | null | undefined): string {
  if (!name) return '?'
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  if (parts.length === 1) {
    return parts[0].slice(0, 2).toUpperCase()
  }
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
}

/**
 * Get relative time string (e.g., "2 hours ago", "3 days ago")
 */
export function getRelativeTime(value: string | Date | null | undefined): string {
  const date = parseDate(value)
  if (!date) return '—'
  try {
    return formatDistanceToNow(date, { addSuffix: true })
  } catch {
    return '—'
  }
}

/**
 * Format a number with compact notation (e.g., 1200 -> "1.2K", 1500000 -> "1.5M")
 */
export function formatCompact(value: number | null | undefined): string {
  if (value === null || value === undefined || isNaN(value)) return '0'
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}K`
  return String(value)
}

/**
 * Format a percentage (e.g., 0.75 -> "75%", 75 -> "75%")
 */
export function formatPercent(value: number | null | undefined, decimals = 1): string {
  if (value === null || value === undefined || isNaN(value)) return '0%'
  const pct = value > 1 && value <= 100 ? value : value * 100
  return `${pct.toFixed(decimals)}%`
}

/**
 * Format a file size in human-readable format
 */
export function formatFileSize(bytes: number | null | undefined): string {
  if (!bytes) return '0 B'
  const units = ['B', 'KB', 'MB', 'GB', 'TB']
  const i = Math.floor(Math.log(bytes) / Math.log(1024))
  return `${(bytes / Math.pow(1024, i)).toFixed(2)} ${units[i]}`
}

/**
 * Format a date to ISO YYYY-MM-DD for form inputs
 */
export function toISODate(value: string | Date | null | undefined): string {
  const date = parseDate(value)
  if (!date) return ''
  return format(date, 'yyyy-MM-dd')
}

/**
 * Parse a YYYY-MM-DD string into a Date object
 */
export function fromISODate(value: string | null | undefined): Date | null {
  if (!value) return null
  const parsed = parseISO(value)
  return isValid(parsed) ? parsed : null
}
